/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useEffect, useRef, useState} from "react"
import {Radio} from "antd"
import {circle} from "@turf/circle"
import mapboxgl from "mapbox-gl"
import {useUpdateEffect} from "react-use"
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder"
import "mapbox-gl/dist/mapbox-gl.css"
import "@mapbox/mapbox-gl-geocoder/lib/mapbox-gl-geocoder.css"
import {GeoLocationInfo} from "types/attendance"
import stylesMap from "./GeoLocationMaps.module.css"

mapboxgl.workerUrl = `/mapbox-gl-csp-worker.js`
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN

const MIN_RADIUS_FEET = 50
const CIRCLE_SOURCE_ID = "geolocation-radius-circle"
const CIRCLE_FILL_LAYER_ID = "geolocation-radius-circle-fill"
const CIRCLE_LINE_LAYER_ID = "geolocation-radius-circle-line"

export type GeoLocationMapItem = {
    latitude: number
    longitude: number
    radiusFeet?: number
}

type Props = {
    geoLocation: GeoLocationInfo
    geoLocations?: GeoLocationMapItem[]
    defaultGeoLocation?: GeoLocationInfo
    styles?: React.CSSProperties
    radius?: number
    forceUpdateLocation?: boolean
    showSearch?: boolean
    onSelectGeoLocation?: (geoLocation: GeoLocationInfo) => void
}

export const GeoLocationMaps = ({
    geoLocation,
    geoLocations,
    defaultGeoLocation = {longitude: -87.6263, latitude: 41.8758},
    radius,
    onSelectGeoLocation,
    styles,
    forceUpdateLocation,
    showSearch = true
}: Props) => {
    const MAP_STYLE_OPTIONS = [
        {id: "streets-v12", label: "Streets", url: "mapbox://styles/mapbox/streets-v12"},
        {id: "satellite-v9", label: "Satellite", url: "mapbox://styles/mapbox/satellite-v9"},
        {id: "satellite-streets-v12", label: "Satellite + Streets", url: "mapbox://styles/mapbox/satellite-streets-v12"}
    ] as const
    const DEFAULT_MAP_STYLE_ID = MAP_STYLE_OPTIONS[0].id
    const mapContainerRef = useRef(null)
    const mapRef = useRef(null)
    const markerRef = useRef(null)
    const markersRef = useRef<mapboxgl.Marker[]>([])
    const radiusRef = useRef(radius)
    const geoLocationRef = useRef(geoLocation)
    const geoLocationsRef = useRef(geoLocations)
    const defaultGeoLocationRef = useRef(defaultGeoLocation)
    const geoLocationsBoundsKeyRef = useRef("")
    const geoLocationsFlyKeyRef = useRef("")
    const skipInitialGeoLocationsFlyRef = useRef(true)
    const [mapStyleId, setMapStyleId] = useState<string>(DEFAULT_MAP_STYLE_ID)
    const skipInitialStyleEffectRef = useRef(true)
    const syncScheduledRef = useRef(false)

    radiusRef.current = radius
    geoLocationRef.current = geoLocation
    geoLocationsRef.current = geoLocations
    defaultGeoLocationRef.current = defaultGeoLocation

    const mapStyleUrl = MAP_STYLE_OPTIONS.find((opt) => opt.id === mapStyleId)?.url ?? MAP_STYLE_OPTIONS[0].url

    const isValidCoordinate = (value: number | null | undefined) => value != null && Number.isFinite(Number(value))

    const clearMarkers = () => {
        markersRef.current.forEach((marker) => marker.remove())
        markersRef.current = []
    }

    const removeCircleOverlay = (map: mapboxgl.Map) => {
        if (map.getLayer(CIRCLE_LINE_LAYER_ID)) {
            map.removeLayer(CIRCLE_LINE_LAYER_ID)
        }
        if (map.getLayer(CIRCLE_FILL_LAYER_ID)) {
            map.removeLayer(CIRCLE_FILL_LAYER_ID)
        }
        if (map.getSource(CIRCLE_SOURCE_ID)) {
            map.removeSource(CIRCLE_SOURCE_ID)
        }
    }

    const ensureCircleOverlay = (map: mapboxgl.Map, data: GeoJSON.Feature | GeoJSON.FeatureCollection) => {
        if (map.getSource(CIRCLE_SOURCE_ID)) {
            ;(map.getSource(CIRCLE_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(data as any)
        } else {
            map.addSource(CIRCLE_SOURCE_ID, {
                type: "geojson",
                data
            })
        }

        if (!map.getLayer(CIRCLE_FILL_LAYER_ID)) {
            map.addLayer({
                id: CIRCLE_FILL_LAYER_ID,
                type: "fill",
                source: CIRCLE_SOURCE_ID,
                paint: {
                    "fill-color": "#1e90ff",
                    "fill-opacity": 0.2
                }
            })
        }
        if (!map.getLayer(CIRCLE_LINE_LAYER_ID)) {
            map.addLayer({
                id: CIRCLE_LINE_LAYER_ID,
                type: "line",
                source: CIRCLE_SOURCE_ID,
                paint: {
                    "line-color": "#1e90ff",
                    "line-width": 2
                }
            })
        }
    }

    const syncMapOverlays = useCallback(() => {
        const map = mapRef.current as mapboxgl.Map | null
        if (!map) return

        // Mutating sources/layers before the style is ready throws "Style is not done loading".
        // Defer via idle so the radius circle is restored after setStyle() finishes.
        if (!map.isStyleLoaded()) {
            if (!syncScheduledRef.current) {
                syncScheduledRef.current = true
                map.once("idle", () => {
                    syncScheduledRef.current = false
                    syncMapOverlays()
                })
            }
            return
        }

        syncScheduledRef.current = false
        clearMarkers()

        const multiLocations =
            geoLocationsRef.current
                ?.filter((item) => isValidCoordinate(item?.longitude) && isValidCoordinate(item?.latitude))
                .map((item) => ({
                    longitude: Number(item.longitude),
                    latitude: Number(item.latitude),
                    radiusFeet:
                        item.radiusFeet !== null && !Number.isNaN(item.radiusFeet)
                            ? Number(item.radiusFeet)
                            : radiusRef.current
                })) ?? []

        if (multiLocations.length > 0) {
            multiLocations.forEach((item) => {
                const marker = new mapboxgl.Marker({color: "#1e90ff"})
                    .setLngLat([item.longitude, item.latitude])
                    .addTo(map)
                markersRef.current.push(marker)
            })

            const features = multiLocations
                .filter((item) => item.radiusFeet !== null && !Number.isNaN(item.radiusFeet))
                .map((item) =>
                    circle([item.longitude, item.latitude], Math.max(item.radiusFeet, MIN_RADIUS_FEET), {
                        steps: 64,
                        units: "feet"
                    })
                )

            if (features.length === 0) {
                removeCircleOverlay(map)
                return
            }

            ensureCircleOverlay(map, {
                type: "FeatureCollection",
                features
            })
            return
        }

        const radiusValue = radiusRef.current
        if (radiusValue == null || Number.isNaN(radiusValue)) {
            removeCircleOverlay(map)
            return
        }

        const effectiveRadiusFeet = Math.max(radiusValue, MIN_RADIUS_FEET)
        const geo = geoLocationRef.current
        const defGeo = defaultGeoLocationRef.current
        const hasGeo = isValidCoordinate(geo?.longitude) && isValidCoordinate(geo?.latitude)
        const centerLng = hasGeo ? geo.longitude : defGeo.longitude
        const centerLat = hasGeo ? geo.latitude : defGeo.latitude

        const geojsonCircle = circle([centerLng, centerLat], effectiveRadiusFeet, {
            steps: 64,
            units: "feet"
        })

        ensureCircleOverlay(map, geojsonCircle as GeoJSON.Feature)
    }, [])

    useEffect(() => {
        const firstMultiLocation =
            geoLocations?.find((item) => isValidCoordinate(item?.longitude) && isValidCoordinate(item?.latitude)) ??
            null

        if (firstMultiLocation) {
            initializeMap(firstMultiLocation.longitude, firstMultiLocation.latitude)
        } else if (isValidCoordinate(geoLocation?.longitude) && isValidCoordinate(geoLocation?.latitude)) {
            initializeMap(geoLocation.longitude, geoLocation.latitude)
        } else {
            initializeMap(defaultGeoLocation.longitude, defaultGeoLocation.latitude)
        }
    }, [])

    useUpdateEffect(() => {
        if (geoLocations?.length) return

        if (
            isValidCoordinate(geoLocation?.longitude) &&
            isValidCoordinate(geoLocation?.latitude) &&
            forceUpdateLocation &&
            markerRef.current
        ) {
            markerRef.current.setLngLat([geoLocation.longitude, geoLocation.latitude])
            mapRef.current.flyTo({
                center: [geoLocation.longitude, geoLocation.latitude],
                essential: true
            })
        }
    }, [geoLocation?.longitude, geoLocation?.latitude, markerRef.current])

    useEffect(() => {
        if (!mapRef.current) return
        if (skipInitialStyleEffectRef.current) {
            skipInitialStyleEffectRef.current = false
            return
        }
        // style.load → syncMapOverlays (defers to idle if needed) restores the circle
        mapRef.current.setStyle(mapStyleUrl)
    }, [mapStyleUrl])

    useEffect(() => {
        syncMapOverlays()
    }, [geoLocation?.longitude, geoLocation?.latitude, radius, geoLocations, syncMapOverlays])

    useEffect(() => {
        const map = mapRef.current
        if (!map || !geoLocations?.length) return

        const validLocations = geoLocations.filter(
            (item) => isValidCoordinate(item?.longitude) && isValidCoordinate(item?.latitude)
        )
        const boundsKey = validLocations.map((item) => `${item.longitude}:${item.latitude}`).join("|")

        // Avoid re-fitting when parent recreates the same locations array,
        // which can override flyTo and leave the camera between points.
        if (!boundsKey || boundsKey === geoLocationsBoundsKeyRef.current) return
        geoLocationsBoundsKeyRef.current = boundsKey

        const bounds = new mapboxgl.LngLatBounds()
        validLocations.forEach((item) => {
            bounds.extend([item.longitude, item.latitude])
        })

        if (!bounds.isEmpty()) {
            map.fitBounds(bounds, {padding: 40, duration: 400, maxZoom: 15})
        }
    }, [geoLocations])

    useEffect(() => {
        const map = mapRef.current
        if (!map || !geoLocations?.length) return

        const validLocations = geoLocations.filter(
            (item) => isValidCoordinate(item?.longitude) && isValidCoordinate(item?.latitude)
        )
        const latestLocation = validLocations[validLocations.length - 1]
        if (!latestLocation) return

        const flyKey = `${latestLocation.longitude}:${latestLocation.latitude}`

        if (skipInitialGeoLocationsFlyRef.current) {
            skipInitialGeoLocationsFlyRef.current = false
            geoLocationsFlyKeyRef.current = flyKey
            return
        }

        if (flyKey === geoLocationsFlyKeyRef.current) return
        geoLocationsFlyKeyRef.current = flyKey

        map.flyTo({
            center: [latestLocation.longitude, latestLocation.latitude],
            essential: true,
            duration: 1000
        })
    }, [geoLocations])

    const initializeMap = (initialLng, initialLat) => {
        if (mapRef.current) return

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapStyleUrl,

            center: [initialLng, initialLat],
            zoom: 12
        })

        mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right")

        mapRef.current.on("load", () => {
            syncMapOverlays()
        })
        mapRef.current.on("style.load", () => {
            syncMapOverlays()
        })

        if (
            !geoLocations?.length &&
            isValidCoordinate(geoLocation?.longitude) &&
            isValidCoordinate(geoLocation?.latitude)
        ) {
            markerRef.current = new mapboxgl.Marker({color: "#1e90ff"})
                .setLngLat([initialLng, initialLat])
                .addTo(mapRef.current)
        }

        if (showSearch && mapboxgl.accessToken) {
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl,
                marker: false,
                placeholder: "Search location",
                flyTo: true
            })
            mapRef.current.addControl(geocoder, "top-left")

            geocoder.on("result", (e: {result}) => {
                const lngLat = getLngLatFromGeocoderResult(e.result)
                if (!lngLat) return
                const [lng, lat] = lngLat
                if (onSelectGeoLocation) {
                    onSelectGeoLocation({latitude: lat, longitude: lng})
                }
                if (markerRef.current) {
                    markerRef.current.setLngLat([lng, lat])
                } else {
                    markerRef.current = new mapboxgl.Marker({color: "#1e90ff"})
                        .setLngLat([lng, lat])
                        .addTo(mapRef.current)
                }
            })
        }

        if (onSelectGeoLocation && !geoLocations?.length) {
            mapRef.current.on("click", (e) => {
                const {lng, lat} = e.lngLat
                onSelectGeoLocation({latitude: lat, longitude: lng})
                if (markerRef.current) {
                    markerRef.current.setLngLat([lng, lat])
                } else {
                    markerRef.current = new mapboxgl.Marker({color: "#1e90ff"})
                        .setLngLat([lng, lat])
                        .addTo(mapRef.current)
                }
            })
        }
    }

    const getLngLatFromGeocoderResult = (result: {
        geometry?: {coordinates?: number[]}
        center?: number[]
        user_coordinates?: number[]
    }): [number, number] | null => {
        const coords = result.geometry?.coordinates
        if (coords && coords.length >= 2) {
            return [coords[0], coords[1]]
        }
        if (result.center?.length >= 2) {
            return [result.center[0], result.center[1]]
        }
        if (result.user_coordinates?.length >= 2) {
            return [result.user_coordinates[0], result.user_coordinates[1]]
        }
        return null
    }

    return (
        <div className={stylesMap.mapContainer}>
            <div ref={mapContainerRef} style={{width: "100%", height: 500, borderRadius: "8px", ...styles}} />
            <div className={stylesMap.mapStyleSelector}>
                <Radio.Group
                    value={mapStyleId}
                    onChange={(e) => setMapStyleId(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="small">
                    {MAP_STYLE_OPTIONS.map((opt) => (
                        <Radio.Button key={opt.id} value={opt.id}>
                            {opt.label}
                        </Radio.Button>
                    ))}
                </Radio.Group>
            </div>
        </div>
    )
}
